/**
 * BarcodeScannerDialog · 2026-10-22
 *
 * Mobile-first scanner for product codes. Three modes in a single dialog:
 *   1. **Live camera barcode / QR scan** via html5-qrcode (UPC/EAN/Code128/QR …).
 *      Scans continuously, auto-closes on first detection.
 *   2. **Photo OCR fallback** — user snaps a printed SKU label; the backend
 *      sends it to Gemini 3 Flash and returns the extracted alphanumeric
 *      code. Useful when the label has no barcode.
 *   3. **Manual input** — tiny text field so the user can type if everything
 *      else fails (and so the dialog is testable without camera permission).
 *
 * On a successful read ``onDetect(code)`` fires and the dialog closes. The
 * parent (``ProductsList``) just writes the code into the search box.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, Image as ImageIcon, Loader2, X, Keyboard, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { maybeCompressImage } from "@/lib/compressImage";

const QR_READER_ID = "quotesys-qrcode-reader";

export const BarcodeScannerDialog = ({ open, onOpenChange, onDetect }) => {
  const { t } = useLanguage();
  const scannerRef = useRef(null);
  // 2026-10-24 · 用户要求默认为「拍照识别」(ocr) —— 多数场景拍一张标签比
  // 启动摄像头实时扫码更稳。live-scan 和手输作为备用模式保留。
  //
  // 2026-10-25 · OCR 升级为「取景框 + 即拍即传」—— 原来调原生相机，用户拍完
  // 整张照片上传 Gemini，含大量背景干扰且图大。改为：
  //   1) 在对话框内直接 getUserMedia 显示预览
  //   2) 中央一个可见的「对准货号」crop 框
  //   3) 用户把货号塞进框内按快门
  //   4) 只截取 crop 框范围的像素 → 上传 → OCR
  //   内容窄 + 分辨率高 = 更准 + 更小 + 更快
  const [mode, setMode] = useState("ocr"); // ocr | scan | manual
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [manualText, setManualText] = useState("");
  const fileInputRef = useRef(null);
  // 2026-10-25 · OCR 取景相关 refs
  const videoRef = useRef(null);
  const videoFrameRef = useRef(null); // 外层容器，用来计算 crop 框相对视频的位置
  const cropFrameRef = useRef(null);   // 中央的 crop 框 DOM
  const ocrStreamRef = useRef(null);   // 当前 MediaStream，关闭时 stop() 所有 track
  const [ocrCamStarted, setOcrCamStarted] = useState(false);

  // 每次打开对话框都复位为 OCR 模式，避免上次切到扫码后再打开又进实时扫
  useEffect(() => {
    if (open) {
      setMode("ocr");
      setManualText("");
    }
  }, [open]);

  const stopScanner = useCallback(async () => {
    const inst = scannerRef.current;
    if (!inst) return;
    try {
      if (inst.isScanning) await inst.stop();
      await inst.clear();
    } catch (_e) {
      /* best-effort cleanup */
    }
    scannerRef.current = null;
    setRunning(false);
  }, []);

  // 2026-10-25 · OCR 取景相机的开关
  const stopOcrCamera = useCallback(() => {
    const s = ocrStreamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      ocrStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setOcrCamStarted(false);
  }, []);

  const emit = useCallback(async (code) => {
    const clean = (code || "").trim();
    if (!clean) return;
    await stopScanner();
    stopOcrCamera();
    onDetect?.(clean);
    onOpenChange?.(false);
  }, [onDetect, onOpenChange, stopScanner, stopOcrCamera]);

  // Boot the live scanner whenever the dialog opens in "scan" mode.
  //
  // 2026-10-24 · 更鲁棒的摄像头启动：
  //   1. 等 Radix Dialog 动画结束再 init（避免 target div 还没真实尺寸）
  //   2. 先 enumerate 摄像头，优先挑背面（label 含 "back"/"environment"），
  //      iOS Safari 对裸 ``facingMode: environment`` 支持有历史问题
  //   3. 如果 enumerate 失败再回退到 ``facingMode`` 约束
  //   4. toast 把真实错误带出来，用户能自助排查（权限 vs HTTPS vs 无相机）
  useEffect(() => {
    if (!open || mode !== "scan") return undefined;
    let cancelled = false;

    const pickCamera = async () => {
      try {
        const cams = await Html5Qrcode.getCameras();
        if (!cams?.length) return null;
        const back = cams.find((c) => /back|rear|environment|后置|背面/i.test(c.label));
        return back || cams[cams.length - 1];
      } catch {
        return null;
      }
    };

    const start = async () => {
      setStarting(true);
      // Let Radix finish its open animation and the viewfinder div paint —
      // without this the ``getElementById`` lookup inside Html5Qrcode often
      // finds a 0×0 element on iOS and bails silently.
      await new Promise((r) => setTimeout(r, 250));
      if (cancelled) return;

      // Explicitly nudge the permission prompt — Chrome Android sometimes
      // needs a direct ``getUserMedia`` call before html5-qrcode's attempt.
      try {
        if (navigator.mediaDevices?.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
          });
          // Release immediately — html5-qrcode will grab its own stream.
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch (permErr) {
        if (!cancelled) {
          const name = permErr?.name || "";
          const msg = name === "NotAllowedError"
            ? (t("scanner.permDenied") || "请在浏览器设置里允许访问摄像头")
            : name === "NotFoundError"
              ? (t("scanner.noCamera") || "未检测到可用摄像头")
              : name === "NotReadableError"
                ? (t("scanner.cameraBusy") || "摄像头被其他程序占用，请关闭后重试")
                : (t("scanner.cameraDenied") || "无法启动摄像头") + ` (${name || permErr.message || "unknown"})`;
          toast.error(msg);
          setStarting(false);
        }
        return;
      }

      try {
        const inst = new Html5Qrcode(QR_READER_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
          ],
          verbose: false,
        });
        scannerRef.current = inst;

        const cam = await pickCamera();
        const cameraTarget = cam?.id || { facingMode: { ideal: "environment" } };
        await inst.start(
          cameraTarget,
          { fps: 10, qrbox: { width: 240, height: 180 } },
          (decoded) => { if (!cancelled) emit(decoded); },
          () => { /* decode fail — ignored, continuous scan */ },
        );
        if (cancelled) await stopScanner();
        else setRunning(true);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.message || e?.name || String(e);
          toast.error((t("scanner.cameraDenied") || "无法启动摄像头") + `: ${detail}`);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    start();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [open, mode, emit, stopScanner, t]);

  // Always stop the camera when the dialog unmounts/closes.
  useEffect(() => {
    if (!open) stopScanner();
    if (!open) stopOcrCamera();
  }, [open, stopScanner, stopOcrCamera]);

  // 2026-10-25 · OCR 预览相机启动 —— mode=ocr 时自动开后置摄像头，mode 切换
  // 或关闭对话框时自动关掉。沿用和 scan 模式一样的 pickCamera 兜底策略。
  useEffect(() => {
    if (!open || mode !== "ocr") return undefined;
    let cancelled = false;
    const start = async () => {
      setStarting(true);
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            // 2026-10-25 · 提高原始流分辨率；crop 完还是小图，但高分辨率
            // 下的 crop 对小字 SKU 更清晰
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        ocrStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          try { await videoRef.current.play(); } catch (_e) { /* iOS autoplay quirk */ }
        }
        setOcrCamStarted(true);
      } catch (e) {
        if (!cancelled) {
          const name = e?.name || "";
          const msg = name === "NotAllowedError"
            ? (t("scanner.permDenied") || "请在浏览器设置里允许访问摄像头")
            : name === "NotFoundError"
              ? (t("scanner.noCamera") || "未检测到可用摄像头")
              : name === "NotReadableError"
                ? (t("scanner.cameraBusy") || "摄像头被其他程序占用，请关闭后重试")
                : (t("scanner.cameraDenied") || "无法启动摄像头") + ` (${name || e.message || "unknown"})`;
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    start();
    return () => { cancelled = true; stopOcrCamera(); };
  }, [open, mode, stopOcrCamera, t]);

  // 2026-10-25 · 按下快门 —— 从 <video> 当前帧按屏幕 crop 框的比例截取一块
  // 像素，以 JPEG 0.85 质量输出。因为 crop 框很窄（通常 400×120），输出
  // ~20-50KB，比全画幅压缩版还小。
  const captureFromCrop = async () => {
    const video = videoRef.current;
    const container = videoFrameRef.current;
    const frame = cropFrameRef.current;
    if (!video || !container || !frame || !video.videoWidth) {
      toast.error(t("scanner.cameraDenied") || "摄像头未就绪");
      return;
    }
    setOcrBusy(true);
    const t0 = performance.now();
    try {
      // 屏幕上的 crop 框 vs 容器的比例 → 映射到 video 原始像素
      const cBox = container.getBoundingClientRect();
      const fBox = frame.getBoundingClientRect();
      const relX = (fBox.left - cBox.left) / cBox.width;
      const relY = (fBox.top - cBox.top) / cBox.height;
      const relW = fBox.width / cBox.width;
      const relH = fBox.height / cBox.height;

      // video 和容器可能是 object-cover，所以要考虑裁切。假设容器与 video
      // 都 object-cover，取 min 轴，center-crop。
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const containerAspect = cBox.width / cBox.height;
      const videoAspect = vw / vh;
      let drawW, drawH, drawX, drawY;
      if (videoAspect > containerAspect) {
        // video 更宽，左右被裁
        drawH = vh;
        drawW = vh * containerAspect;
        drawX = (vw - drawW) / 2;
        drawY = 0;
      } else {
        drawW = vw;
        drawH = vw / containerAspect;
        drawX = 0;
        drawY = (vh - drawH) / 2;
      }
      const sx = drawX + relX * drawW;
      const sy = drawY + relY * drawH;
      const sw = relW * drawW;
      const sh = relH * drawH;

      // 输出 canvas —— 保持原始像素，不额外缩放
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("canvas.toBlob failed");
      console.log(`[scan-code] crop ${canvas.width}x${canvas.height} = ${blob.size}B in ${(performance.now() - t0).toFixed(0)}ms`);

      const t1 = performance.now();
      const form = new FormData();
      form.append("file", new File([blob], "sku-crop.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/ai/scan-code", form, { timeout: 45000 });
      console.log(`[scan-code] upload+ocr took ${(performance.now() - t1).toFixed(0)}ms, total ${(performance.now() - t0).toFixed(0)}ms`);
      if (data?.code) {
        toast.success(`${t("scanner.recognized") || "识别到"}: ${data.code}`);
        await emit(data.code);
      } else {
        toast.warning(t("scanner.notRecognized") || "没有识别到货号，请把货号对准框内重拍");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (t("scanner.ocrFailed") || "OCR 失败"));
    } finally {
      setOcrBusy(false);
    }
  };

  const handleOcrUpload = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setOcrBusy(true);
    const t0 = performance.now();
    try {
      // 2026-10-24 · 极致压缩 —— 用户要求总耗时 < 1s。SKU 文字高对比度，
      // 480px / 质量 0.5 完全够 Gemini 认出，80KB 包体上传 <150ms。
      const compressed = await maybeCompressImage(file, {
        maxEdge: 480,
        quality: 0.5,
        minBytes: 0,
        targetBytes: 80 * 1024,
      });
      console.log(`[scan-code] compressed ${file.size}B → ${compressed.size}B in ${(performance.now() - t0).toFixed(0)}ms`);
      const t1 = performance.now();
      const form = new FormData();
      form.append("file", compressed);
      const { data } = await api.post("/ai/scan-code", form, { timeout: 45000 });
      console.log(`[scan-code] upload+ocr took ${(performance.now() - t1).toFixed(0)}ms, total ${(performance.now() - t0).toFixed(0)}ms`);
      if (data?.code) {
        toast.success(`${t("scanner.recognized") || "识别到"}: ${data.code}`);
        await emit(data.code);
      } else {
        toast.warning(t("scanner.notRecognized") || "没有识别到货号，请换张清晰的图片或手动输入");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (t("scanner.ocrFailed") || "OCR 失败"));
    } finally {
      setOcrBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopScanner(); stopOcrCamera(); } onOpenChange?.(o); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="scanner-dialog">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <Zap size={16} className="text-brand" /> {t("scanner.title") || "扫描货号"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === "ocr"
              ? (t("scanner.ocrFrameHint") || "把货号对准方框内，按下圆钮拍摄。只识别框内文字，速度更快。")
              : mode === "scan"
                ? (t("scanner.hint") || "把条码 / 二维码对准取景框，自动识别后填入搜索。")
                : (t("scanner.manualHint") || "手动输入货号，回车确认。")}
          </DialogDescription>
        </DialogHeader>

        {/* 2026-10-25 · OCR 模式 —— 内嵌预览相机 + 中央 crop 对准框 */}
        {mode === "ocr" && (
          <div className="relative bg-black" data-testid="scanner-ocr-wrapper">
            <div ref={videoFrameRef} className="relative w-full aspect-[4/3] overflow-hidden">
              <video
                ref={videoRef}
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="scanner-ocr-video"
              />
              {/* 启动中遮罩 */}
              {(starting || !ocrCamStarted) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 text-xs gap-2 bg-black/40">
                  <Loader2 size={28} className="animate-spin" />
                  <span>{t("scanner.starting") || "摄像头启动中…"}</span>
                </div>
              )}
              {/* 中央 crop 框 —— 窄条，专门对准单行货号 */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                {/* 半透明暗罩（上下左右） —— 突出 crop 框 */}
                <div className="absolute inset-0 bg-black/45" />
                <div
                  ref={cropFrameRef}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[76%] h-[36%] rounded-md border-2 border-brand shadow-[0_0_0_9999px_rgba(0,0,0,0.0)] bg-transparent"
                  data-testid="scanner-ocr-crop-frame"
                  style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }}
                >
                  {/* 四个角标 */}
                  <span className="absolute -top-0.5 -left-0.5 w-5 h-5 border-l-2 border-t-2 border-white" />
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 border-r-2 border-t-2 border-white" />
                  <span className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-l-2 border-b-2 border-white" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-r-2 border-b-2 border-white" />
                </div>
                <div className="absolute left-0 right-0 bottom-3 text-center text-white/90 text-[11px] font-medium">
                  {t("scanner.alignHint") || "把货号对准方框内，按下方圆钮拍摄"}
                </div>
              </div>
            </div>
            {/* 快门按钮 */}
            <div className="flex items-center justify-center py-3 bg-black">
              <button
                type="button"
                onClick={captureFromCrop}
                disabled={!ocrCamStarted || ocrBusy}
                className="w-16 h-16 rounded-full bg-white border-4 border-white/70 shadow-lg flex items-center justify-center active:scale-95 transition disabled:opacity-50"
                data-testid="scanner-ocr-capture"
                aria-label={t("scanner.capture") || "拍照"}
              >
                {ocrBusy ? (
                  <Loader2 size={28} className="animate-spin text-brand" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Live scanner viewfinder */}
        {mode === "scan" && (
          <div className="relative bg-black" data-testid="scanner-viewfinder">
            <div id={QR_READER_ID} className="w-full aspect-[4/3]" />
            {(starting || !running) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 text-xs gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span>{t("scanner.starting") || "摄像头启动中…"}</span>
              </div>
            )}
          </div>
        )}

        {/* Manual input */}
        {mode === "manual" && (
          <div className="px-4 py-6 space-y-3" data-testid="scanner-manual-wrapper">
            <Input
              autoFocus
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") emit(manualText); }}
              placeholder={t("scanner.manualPlaceholder") || "输入货号后回车"}
              className="h-11 font-mono text-sm"
              data-testid="scanner-manual-input"
            />
            <Button
              className="w-full h-11 rounded-md bg-brand hover:bg-brand-hover"
              onClick={() => emit(manualText)}
              disabled={!manualText.trim()}
              data-testid="scanner-manual-confirm"
            >
              {t("common.confirm") || "确认"}
            </Button>
          </div>
        )}

        {/* Mode switcher + close */}
        <div className="px-3 py-3 border-t border-border bg-card flex items-center gap-2" data-testid="scanner-toolbar">
          <Button
            variant={mode === "ocr" ? "default" : "outline"}
            size="sm"
            onClick={async () => { await stopScanner(); setMode("ocr"); }}
            className={`h-10 rounded-md flex-1 ${mode === "ocr" ? "bg-brand hover:bg-brand-hover" : ""}`}
            data-testid="scanner-mode-ocr"
          >
            <ImageIcon size={14} /> {t("scanner.ocr") || "拍照识别"}
          </Button>
          <Button
            variant={mode === "scan" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("scan")}
            className={`h-10 rounded-md flex-1 ${mode === "scan" ? "bg-brand hover:bg-brand-hover" : ""}`}
            data-testid="scanner-mode-scan"
          >
            <Camera size={14} /> {t("scanner.live") || "扫码"}
          </Button>
          <Button
            variant={mode === "manual" ? "default" : "outline"}
            size="sm"
            onClick={async () => { await stopScanner(); setMode("manual"); }}
            className={`h-10 rounded-md flex-1 ${mode === "manual" ? "bg-brand hover:bg-brand-hover" : ""}`}
            data-testid="scanner-mode-manual"
          >
            <Keyboard size={14} /> {t("scanner.manual") || "手输"}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange?.(false)}
            className="h-10 w-10 shrink-0"
            data-testid="scanner-close"
          >
            <X size={16} />
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleOcrUpload}
          data-testid="scanner-ocr-file-input"
        />
      </DialogContent>
    </Dialog>
  );
};
